// Builds a shareable PNG card from a Sara reply using canvas only (no image libraries).
import { cleanMessage } from './renderMarkdown.tsx';

const WIDTH = 1080;
const PAD = 64;
const MAX_CHARS = 1400;

export interface SaraCardInput {
  answer: string;
  stock?: string | null;
  question?: string;
}

const stripInlineMarkdown = (line: string) =>
  line
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/\|/g, ' · ')
    .trim();

const toCardLines = (text: string): string[] =>
  cleanMessage(text)
    .split('\n')
    .map(l => l.replace(/^\s*[*•-]\s+/, '• ').replace(/\s+/g, ' ').trim())
    .filter(l => l && !/^[-—=_]{3,}$/.test(l))
    .map(stripInlineMarkdown);

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const out: string[] = [];
  let line = '';
  for (const w of words) {
    const candidate = line ? `${line} ${w}` : w;
    if (ctx.measureText(candidate).width > maxWidth && line) {
      out.push(line);
      line = w;
    } else {
      line = candidate;
    }
  }
  if (line) out.push(line);
  return out;
}

const lineColor = (line: string) =>
  line.includes('🟢') || /صاعد|شراء|إيجاب/.test(line) ? '#34d399'
    : line.includes('🔴') || /هابط|بيع|سلب/.test(line) ? '#fb7185'
      : '#e5e7eb';

export async function buildSaraShareCard({ answer, stock, question }: SaraCardInput): Promise<Blob> {
  const bodyLines = toCardLines(answer).join('\n').slice(0, MAX_CHARS).split('\n');

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  ctx.direction = 'rtl';
  ctx.font = '30px "Cairo", "Segoe UI", Tahoma, sans-serif';

  const maxWidth = WIDTH - PAD * 2;
  const wrapped = bodyLines.flatMap(l => wrap(ctx, l, maxWidth));
  const lineHeight = 46;
  const headerHeight = 150;
  const questionHeight = question ? 70 : 0;
  const footerHeight = 110;
  canvas.width = WIDTH;
  canvas.height = headerHeight + questionHeight + wrapped.length * lineHeight + footerHeight;

  const bg = ctx.createLinearGradient(0, 0, WIDTH, canvas.height);
  bg.addColorStop(0, '#0A1021');
  bg.addColorStop(1, '#1a1145');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, WIDTH, canvas.height);
  ctx.strokeStyle = 'rgba(139,92,246,0.55)';
  ctx.lineWidth = 3;
  ctx.strokeRect(18, 18, WIDTH - 36, canvas.height - 36);

  // Header
  ctx.textAlign = 'right';
  ctx.fillStyle = '#C084FC';
  ctx.font = 'bold 42px "Cairo", "Segoe UI", Tahoma, sans-serif';
  ctx.fillText('سارة • TAD-AI', WIDTH - PAD, 92);
  ctx.font = '26px "Cairo", "Segoe UI", Tahoma, sans-serif';
  ctx.fillStyle = '#94a3b8';
  ctx.textAlign = 'left';
  ctx.fillText(new Date().toLocaleDateString('ar-EG', { day: 'numeric', month: 'long', year: 'numeric' }), PAD, 92);
  ctx.textAlign = 'right';

  let y = headerHeight;
  if (question) {
    ctx.fillStyle = '#8B5CF6';
    ctx.font = '28px "Cairo", "Segoe UI", Tahoma, sans-serif';
    ctx.fillText(`س: ${question.slice(0, 90)}`, WIDTH - PAD, y);
    y += questionHeight;
  }

  ctx.font = '30px "Cairo", "Segoe UI", Tahoma, sans-serif';
  for (const line of wrapped) {
    ctx.fillStyle = lineColor(line);
    ctx.fillText(line, WIDTH - PAD, y + 30);
    y += lineHeight;
  }

  // Footer
  ctx.fillStyle = 'rgba(139,92,246,0.18)';
  ctx.fillRect(PAD, canvas.height - footerHeight + 20, WIDTH - PAD * 2, 1);
  ctx.font = '24px "Cairo", "Segoe UI", Tahoma, sans-serif';
  ctx.fillStyle = '#94a3b8';
  ctx.fillText(stock ? `السهم: ${stock}` : 'تحليل السوق السعودي — تاسي', WIDTH - PAD, canvas.height - 46);
  ctx.textAlign = 'left';
  ctx.fillStyle = '#C084FC';
  ctx.fillText('tad-ai.com', PAD, canvas.height - 46);

  return new Promise(resolve => canvas.toBlob(b => resolve(b!), 'image/png', 0.95));
}

export async function shareSaraCard(input: SaraCardInput): Promise<'shared' | 'downloaded' | 'failed'> {
  try {
    const blob = await buildSaraShareCard(input);
    const file = new File([blob], 'sara-analysis.png', { type: 'image/png' });
    const nav = navigator as any;
    if (nav.canShare?.({ files: [file] })) {
      await nav.share({ files: [file], title: 'تحليل من سارة', text: 'tad-ai.com' });
      return 'shared';
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sara-analysis.png';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    return 'downloaded';
  } catch (e) {
    console.error('Failed to build Sara share card:', e);
    return 'failed';
  }
}
